import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { DATE_FORMAT } from '@/constants';

dayjs.extend(utc);
dayjs.extend(timezone);

export const SUBMISSION_DATE_FORMAT = 'YYYY-MM-DD';

export function formatUtcToLocalDate(dateString: string): string {
  const date = dayjs.utc(dateString);

  if (!date.isValid()) {
    return '';
  }

  return date.local().format(DATE_FORMAT);
}

/**
 * Formats a UTC timestamp in an explicit IANA time zone — used for values the
 * API stores in UTC but that must be read in a location's own time (e.g. an
 * allocation's farm time zone). Falls back to the viewer's local zone when no
 * zone is given or the zone name is not recognized, so a missing/invalid value
 * degrades to a sensible local time instead of throwing.
 */
export function formatUtcInTimeZone(
  date?: string | null,
  timeZone?: string | null,
  format = DATE_FORMAT
): string {
  if (!date) {
    return '';
  }

  const parsed = dayjs.utc(date);

  if (!parsed.isValid()) {
    return '';
  }

  if (!timeZone) {
    return parsed.local().format(format);
  }

  try {
    return parsed.tz(timeZone).format(format);
  } catch {
    return parsed.local().format(format);
  }
}

/**
 * Formats a calendar date — a day with no time and no time zone, such as a
 * planting, harvest, sowing or season date. The API encodes these as
 * UTC-midnight instants (`2026-01-05T00:00:00Z`), so reading them with the
 * viewer's local zone lands on the previous day at any negative UTC offset.
 * This reads the day exactly as the payload states it and never applies an
 * offset. Bare `YYYY-MM-DD` input is handled identically.
 *
 * Use {@link formatUtcInTimeZone} instead for genuine timestamps (`created_at`,
 * `updated_at`, `discarded_at`), where zone conversion is the correct behavior.
 */
export function formatCalendarDate(
  date?: dayjs.ConfigType,
  format = DATE_FORMAT
): string {
  if (!date) {
    return '';
  }

  const parsed = dayjs.utc(date);

  return parsed.isValid() ? parsed.format(format) : '';
}

/**
 * Calendar date as a local dayjs for an antd `DatePicker` value. The picker
 * compares against a local "today" for highlighting and min/max rules, so the
 * day is re-anchored to local midnight rather than handed over as a UTC
 * instant. `undefined` for missing or unparseable input, which clears the
 * picker instead of seeding it with an invalid date.
 */
export function toCalendarDatePickerValue(
  date?: dayjs.ConfigType
): Dayjs | undefined {
  if (!date) {
    return undefined;
  }

  const parsed = dayjs.utc(date);

  return parsed.isValid()
    ? dayjs(parsed.format(SUBMISSION_DATE_FORMAT))
    : undefined;
}

/**
 * Formats an instant in the viewer's local zone — for timestamps such as
 * `created_at`, `exported_at` or a SAP sync moment, where the reader wants the
 * wall-clock time where they are sitting.
 *
 * Not for calendar dates (planting, harvest, sowing, season): those carry no
 * time zone, and converting them drops a day at negative UTC offsets. Use
 * {@link formatCalendarDate} there.
 */
export function formatDate(date?: dayjs.ConfigType, format = DATE_FORMAT) {
  return date ? dayjs(date).format(format) : '';
}

export function formatDateForSubmission(date?: dayjs.ConfigType): string {
  return date ? dayjs(date).format(SUBMISSION_DATE_FORMAT) : '';
}
